import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Menu, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';
import logo from '@/assets/icon.png';

const LANGUAGES = ['es', 'cat', 'en', 'de'];
const LANG_LABELS = { es: 'ES', cat: 'CAT', en: 'EN', de: 'DE' };

export default function Header() {
  const { language, setLanguage } = useLanguage();
  const t = translations[language].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const navLinks = [
    { id: 'services', label: t.services },
    { id: 'tour', label: t.tour },
    { id: 'team', label: t.team },
    { id: 'reviews', label: t.reviews },
    { id: 'contact', label: t.contact },
  ];

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-[#e3e6e8] backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            <img 
              src={logo} 
              alt="Clínica Brandt-Lafuente"
              className="h-12 w-auto object-contain"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="text-sm font-medium text-[#5b6770] hover:text-[#ffa600] transition-colors duration-200 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#ffa600] after:transition-all after:duration-200 hover:after:w-full"
                data-testid={`nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center bg-gray-50 rounded-full p-1" data-testid="language-switcher">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  data-testid={`lang-${lang}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                    language === lang
                      ? 'bg-[#ffa600] text-white shadow-sm'
                      : 'text-[#5b6770] hover:text-[#5b6770]'
                  }`}
                >
                  {LANG_LABELS[lang]}
                </button>
              ))}
            </div>

            <a
              href="tel:+34932121044"
              className="hidden md:flex items-center gap-2 bg-[#ffa600] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#e69500] transition-all duration-200 hover:shadow-md"
              data-testid="header-phone-cta"
            >
              <Phone className="w-4 h-4" />
              932 12 10 44
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-[#5b6770] hover:text-[#5b6770]"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-lg"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="block w-full text-left px-4 py-3 text-[#5b6770] hover:text-[#ffa600] hover:bg-gray-50 rounded-xl text-sm font-medium transition-colors"
                data-testid={`mobile-nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-2 px-4 pt-3 border-t border-gray-100 mt-3">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  onClick={() => { setLanguage(lang); setMobileOpen(false); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    language === lang
                      ? 'bg-[#ffa600] text-white'
                      : 'bg-gray-100 text-[#5b6770]'
                  }`}
                >
                  {LANG_LABELS[lang]}
                </button>
              ))}
            </div>
            <a
              href="tel:+34932121044"
              className="flex items-center justify-center gap-2 mx-4 mt-3 bg-[#ffa600] text-white px-4 py-3 rounded-xl text-sm font-semibold"
              data-testid="mobile-phone-cta"
            >
              <Phone className="w-4 h-4" />
              932 12 10 44
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
